import React from "react";
import ICsvGenerator, {
  IEndpointDataObject,
  IFileLabels,
} from "./ICsvGenerator";
import BttfAxios from "@features/utils/axios";
import fileSaver from "file-saver";
import Loader from "@appComponents/Loader/Loader";
import cryptoCurrencies from "@features/staticData/cryptoCurrencies";
import { classNames as cn } from "@features/utils/reactFunctions";
import {
  formatDate,
  isIsoDate,
  renameMainCurrency,
} from "@features/utils/functions";
import { TransactionTypes } from "@funds/interfaces/ITransaction";
import { labels as transactionHistoryLabels } from "@funds/components/TransactionHistoryTable/components/TransactionHistoryTableRow/TransactionHistoryTableRow";
import { fiatCurrenciesSlug } from "@features/staticData/fiatCurrencies";
import * as style from "./CsvGeneratorStyle.module.scss";

const labels: IFileLabels = {
  details: "Details",
  transaction_id: "Transaction ID",
  in: "In",
  out: "Out",
  slug: "Slug",
  spv_name: "SPV name",
  status: "Status",
  price_per_share: "Price per share",
  number_of_shares: "Number of shares",
  fee_currency: "Fee currency",
  payment_currency: "Payment currency",
  agreement_generated: "Agreement generated",
  date: "Date",
  value: "Value",
  ...TransactionTypes,
  ...transactionHistoryLabels,
};

const errorMessage = "Something went wrong, please try again.";
const noDataMessage = "No data to generate file.";

const CsvGenerator: React.FunctionComponent<ICsvGenerator> = (props) => {
  let csvFileContent = "";
  const [isGenerating, setIsGenerating] = React.useState(false);

  const fetchAllData = () => {
    let finalData: IEndpointDataObject[] = [];
    setIsGenerating(true);

    if (props.baseEndpoint) {
      const params = BttfAxios.paramBuilder({ ...props.endpointDetails });
      BttfAxios.get(
        `${props.baseEndpoint}${params}`,
        (res) => {
          const { total_pages, items } = res.data;
          finalData = props.noPagination ? res.data : items;

          if (!finalData.length) {
            setIsGenerating(false);
            return alert(noDataMessage);
          }

          if (total_pages > 1) {
            const promises = [];

            for (let page = 2; page <= total_pages; page++) {
              const newParams = BttfAxios.paramBuilder({
                ...props.endpointDetails,
                page,
              });
              promises.push(fetchData(newParams));
            }

            Promise.all(promises)
              .then((res: IEndpointDataObject[][]) => {
                finalData = [...finalData, ...res.flat()];
                generateCSV(finalData);
              })
              .catch(() => {
                setIsGenerating(false);
                alert(errorMessage);
              });
          } else {
            generateCSV(finalData);
          }
        },
        () => {
          setIsGenerating(false);
          alert(errorMessage);
        }
      );
    } else if (props.items && props.items.length) {
      generateCSV(props.items);
    } else {
      setIsGenerating(false);
      alert(noDataMessage);
    }
  };

  const fetchData = (params: string) =>
    new Promise((resolve, reject) => {
      BttfAxios.get(
        `${props.baseEndpoint}${params}`,
        (res) => {
          resolve(res.data.items);
        },
        (err) => {
          reject(errorMessage);
        }
      );
    });

  const generateCSV = (array: IEndpointDataObject[]) => {
    array.forEach((obj, index) => {
      if (index === 0) {
        fillFileHeadlines(obj);
      }

      fillFileData(obj);
    });

    const blob = new Blob([addExcelUtf8Support(csvFileContent)], {
      type: "text/csv;encoding:utf-8",
    });

    fileSaver.saveAs(blob, `${props.fileName}.csv`);
    setIsGenerating(false);
  };

  const fillFileHeadlines = (obj: IEndpointDataObject) => {
    Object.keys(obj).forEach((key: string, i: number) => {
      updateFileContentWithProperData(key, i);
    });

    csvFileContent += "\n";
  };

  const fillFileData = (obj: IEndpointDataObject) => {
    Object.keys(obj).forEach((key, i) => {
      updateFileContentWithProperData(obj[key], i);
    });

    csvFileContent += "\n";
  };

  const updateFileContentWithProperData = (
    value: IEndpointDataObject | string | number | null,
    i: number
  ) => {
    let result;
    const stringValue = value === null ? "" : value.toString();

    if (typeof value === "object" && value !== null) {
      result = getObjectValues(value);
    } else if (isIsoDate(stringValue)) {
      result = formatDate(stringValue);
    } else {
      const label = getLabel(stringValue);
      result = label.replace(/"/g, '""');
    }

    result = escapeValue(result);

    if (i > 0) {
      csvFileContent += ",";
    }

    csvFileContent += result;
  };

  const getObjectValues = (obj: IEndpointDataObject) => {
    let objectValues = "";
    const setObjectValues = (
      val: IEndpointDataObject | string | number | null,
      name?: string
    ) => {
      if (val === null) return;

      if (typeof val === "object") {
        Object.keys(val).forEach((key) => {
          const isNestedObject =
            typeof val[key] === "object" && val[key] !== null;

          if (isNestedObject) {
            const label = getLabel(key);
            objectValues += `${label}: \u2028`;
          }

          setObjectValues(val[key], key);

          if (isNestedObject) {
            objectValues += `\u2028`;
          }
        });
      } else {
        let result = val.toString().replace(/"/g, '""');
        const labelName = name ? getLabel(name) : "";
        const labelResult = getLabel(result);

        result = `  ${labelName}: ${labelResult}`;
        objectValues += result + `\u2028`;
      }

      return;
    };

    setObjectValues(obj);
    return objectValues;
  };

  const escapeValue = (value: string) => {
    if (value.search(/("|,|\n)/g) >= 0) {
      return `"${value}"`;
    }

    return value;
  };

  const getLabel = (value: string): string => {
    const label = labels[value];

    if (label) {
      return label as string;
    }

    if (
      cryptoCurrencies.includes(value) ||
      fiatCurrenciesSlug.includes(value)
    ) {
      return renameMainCurrency(value).toUpperCase();
    }

    return value;
  };

  const addExcelUtf8Support = (data: string) => {
    return "\uFEFF" + data;
  };

  return (
    <>
      <button
        onClick={fetchAllData}
        className={
          props.buttomClassname ?? cn(style.csv, isGenerating && style.hide)
        }
        data-cy="csv-generator"
      >
        {!props.customIcon && (
          <span className="fa fa-download" aria-hidden="true" />
        )}
        {props.customIconPosition === "left" && props.customIcon}
        {!props.hideText && "Download CSV"}
        {(!props.customIconPosition || props.customIconPosition === "right") &&
          props.customIcon}
      </button>
      {isGenerating && <Loader fullWidth={true} />}
    </>
  );
};

export default CsvGenerator;
